import * as React from "react";
import { MoneyTextBoxEditor } from "../../../../../../../react-core/Html/Money/MoneyTextBoxEditor";
import { SelectEditor } from "../../../../../../../react-core/Html/SelectEditor";
import { TextBoxEditor } from "../../../../../../../react-core/Html/TextBoxEditor";
import { Numeric } from "../../../../../../../react-core/Html/Validation/RegExPatterns";
import { isNullOrEmpty } from "../../../../../../../react-core/Utils/Arrays";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { Frequency } from "../../../../../../../Wrap/Constants/Enums/Domain/Frequency";
import { SubAccountType } from "../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { DurationType } from "../../../../../../../Wrap/Constants/Enums/DurationType";
import { MoneyOrPercentageType } from "../../../../../../../Wrap/Constants/Enums/MoneyOrPercentageType";
import { Money } from "../../../../../../../Wrap/Core/DataTypes/Money";
import { MoneyHelper } from "../../../../../../Shared/Common/MoneyHelper";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { SectionHeader } from "../../SharedComponents";
import { ChargesHelper } from "../../Helpers/ChargesHelper";

import { AdviserChargesContents, AdviserChargesValidation, ChargeFromMap, ChargeFromTypes } from "../Resource/Contents";
import { FrequencyArraysMap, FrequencyTypeArrays } from "../../Resource/Contents";
import { WizardLinter, EWizardLinterStatus } from "../../../../../../Shared/Process/Process/WizardLinter";
import { EWizardLinterHelper } from "../../../NewBusinessWizard/Resource/EWizardLinterHelper";
import { SourceType } from "../../../../../../../Wrap/Constants/Enums/Domain/SourceType";
import { SubAccountTypeHelper } from "../../Helpers/SubAccountTypeHelper";
import { RadiosEditor } from "../../../../../../../react-core/Html/RadiosEditor";
import { getQueryParameters } from "../../../../../../Shared/Wizards/Entities/QueryParametersHelper";
import { RegularPaymentActionType } from "../../../RegularPaymentWizard/Resource/Contents";

interface IRIACProps extends IProcessComponentProps {
    type: SubAccountType;
}

interface IRIACState {
}

export class RIAC extends ProcessComponent<IRIACProps, IRIACState> {
    constructor(props: any) {
        super(props);
        this.state = {};
    }

    componentDidMount() {
        super.componentDidMount();
        const { data, onChange } = this.props;
        if (data && data.RegularPaymentInitialAdviceCharge) {
            if (isNullOrEmpty(data.RegularPaymentInitialAdviceCharge.Frequency)) {
                this.onDataChange(
                    onChange,
                    { Frequency: Frequency.Monthly, SourceType: SourceType.WrapCash },
                    data,
                    "RegularPaymentInitialAdviceCharge"
                );
            }
        }
    }

    componentDidUpdate() {
        const { data, onChange, type } = this.props;
        const sourceType = (SubAccountTypeHelper.isOnPlatformPension(type) || type === SubAccountType.JuniorSipp) 
            ? SourceType.ProductCash 
            : SourceType.WrapCash;
        if (isNullOrEmpty(data?.RegularPaymentInitialAdviceCharge?.SourceType) || data?.RegularPaymentInitialAdviceCharge?.SourceType == 0) {
            this.onDataChange(onChange, { SourceType: sourceType }, data, "RegularPaymentInitialAdviceCharge");
        }
    }

    existingRegularInitialAdviserCharge(): boolean{
        return !this.props.data.RegularPaymentInitialAdviceCharge.IsFromInstruction 
        && this.props.data.RegularPaymentInitialAdviceCharge.AdviceCharge.Money.Amount > 0;
    }

    riacAmount(): Money {
        const { data } = this.props;
        const riac = data?.RegularPaymentInitialAdviceCharge;
        if (riac && riac.AdviceCharge) {return riac.AdviceCharge.Money;}
        return null;
    }

    riacAmountGiven(): boolean {
        const amount = this.riacAmount();
        return amount && amount.Amount > 0;
    }

    updateAmount(value: number, existingAdviceCharge) {
        const data = {
            AdviceCharge: {
                Type: MoneyOrPercentageType.Money,
                Money: { Amount: value, Currency: MoneyHelper.getDefaultCurrency() },
                SourceType: existingAdviceCharge?.SourceType || SourceType.ProductCash,
            },
        };
        return data;
    }

    updateDuration(value: number) {
        return {
            Duration: {
                DurationType: DurationType.NumberOfPayments,
                NumberOfPayments: value,
            },
        };
    }

    updateSourceType(sourceType, existingAdviceCharge) {
        const data = {
            AdviceCharge: {
                ...existingAdviceCharge,  
                SourceType: sourceType,   
            },
        };
        return data;
    }

    renderAlert() {
        const { data, type } = this.props;
        const riac = this.riacAmount();

        const alerts = [];
        const result:boolean = this.existingRegularInitialAdviserCharge();
        
        //if riac is disable
        if(result)
        {
            alerts.push(
                <Alert
                alertType={AlertType.Warning}
                message={`You can't change the fee when editing a regular payment in. You will need to make any fee changes separately from the Fees and Charges tab.`}
            />
            );
        }
        if (!isNullOrEmpty(alerts)) {
            return alerts;
        }

        if (ChargesHelper.validateRIACAmount(data, type, riac.Amount)) {
            return (
                <Alert
                    alertType={AlertType.Warning}
                    message={AdviserChargesValidation.RIACEqualsOrGreaterThanRegularPayment}
                />
            );
        }
    }
    
    public render(): JSX.Element {
        const { data, onChange, type } = this.props;
        const riac = data?.RegularPaymentInitialAdviceCharge;
        if (!ChargesHelper.hasRegularPayments(data, type)) {
            return null;
        }

        const isEdit = getQueryParameters()["action"] === RegularPaymentActionType.Edit
        return (
            <WizardLinter status={EWizardLinterStatus.PleaseCheck} enabled={EWizardLinterHelper.shouldDisplay(type)}>
                {SectionHeader(
                    AdviserChargesContents.RIAC,
                    "For monetary (£) Initial Adviser Charges – we try to take these straight away if there is cleared cash. For percentage (%) Initial Adviser Charges – we will pay this once the expected cash has been applied."
                )}
                {this.renderAlert()}
                <div className="row">
                    <div className="col-12 col-xs-6 col-sm-4">
                        <MoneyTextBoxEditor
                            label="Amount"
                            isNotForm={true}
                            //tooltip={AdviserChargesContents.RIACTooltip}
                            disabled={this.existingRegularInitialAdviserCharge()}
                            value={this.riacAmount()?.Amount}
                            onChange={(v) => {
                                this.onDataChange(
                                    onChange,
                                    this.updateAmount(v, data?.RegularPaymentInitialAdviceCharge?.AdviceCharge),
                                    data,
                                    "RegularPaymentInitialAdviceCharge"
                                );
                            }}
                            validationRules={[
                                {
                                    errorMessage: AdviserChargesValidation.CannotLessThanZero,
                                    validator: (v) => {
                                        return !v || v > 0;
                                    },
                                },
                            ]}
                            alwaysValidateOnUpdate={true}
                            currency={CurrencyCode.GBP}
                        />
                        <small className="mb-30 mt-4">
                            <strong>
                                £{(riac?.AdviceCharge.Money.Amount * riac?.Duration?.NumberOfPayments).toFixed(2)}
                            </strong>{" "}
                            total adviser charge
                        </small>
                    </div>

                    <div className="col-12 col-xs-6 col-sm-3">
                        <TextBoxEditor
                            label="Repeats"
                            cssWrapper="mb-0"
                            required={this.riacAmountGiven() ? "Please enter a value." : null}
                            disabled={this.existingRegularInitialAdviserCharge()}
                            placeholder=""
                            value={riac?.Duration?.NumberOfPayments}
                            regex={{ errorMessage: "Numeric", value: Numeric }}
                            onChange={(v) => {
                                this.onDataChange(
                                    onChange,
                                    this.updateDuration(Number(v)),
                                    data,
                                    "RegularPaymentInitialAdviceCharge"
                                );
                            }}
                        />
                    </div>
                    <div className="col-12 col-xs-6 col-sm-3">
                        <SelectEditor
                            options={FrequencyTypeArrays}
                            getText={(v) => {
                                return FrequencyArraysMap[v];
                            }}
                            required="Please select frequency"
                            selected={riac ? riac.Frequency : Frequency.Monthly}
                            label="Deduction frequency"
                            disabled={this.existingRegularInitialAdviserCharge()}
                            onChange={(v) => {
                                this.onDataChange(
                                    onChange,
                                    { Frequency: v },
                                    data,
                                    "RegularPaymentInitialAdviceCharge"
                                );
                            }}
                        />
                    </div>
                </div>
                {(SubAccountTypeHelper.isOnPlatformPension(type) || 
                type === SubAccountType.JuniorSipp) && (
                    <RadiosEditor
                        cssWrapper="rg-150 mb-24 mt-30"
                        label={"Take the charge from:"}
                        options={ChargeFromTypes}
                        selected={data?.RegularPaymentInitialAdviceCharge?.SourceType && data.RegularPaymentInitialAdviceCharge.SourceType !== SourceType.NotSpecified 
                            ? data.RegularPaymentInitialAdviceCharge.SourceType 
                            : SourceType.ProductCash}
                        getText={(o) => ChargeFromMap[o]}
                        required={this.riacAmountGiven() ? "required" : null}
                        onChange={(v) => {
                            this.onDataChange(
                                onChange, 
                                { SourceType: v },
                                data,
                                "RegularPaymentInitialAdviceCharge"
                            );
                        }}
                        disabled={this.existingRegularInitialAdviserCharge()}
                    />
                )}
                <hr className="mb-30" />
            </WizardLinter>
        );
    }
}
